import type { TokenUsdPrices } from '@/components/TransactionModule/tradeQuote';

import { amountToUsd } from '@/utils/numbers';

import { Fee, GaslessPrice, Quote, SwapPrice } from './0x.types';
import { trackError } from './errors';
import { routes } from './routes';

/**
 * Event Properties logged to Spindl for Referral Tracking
 */
type MatchaAutoSwapEventProperties = {
  txnHash: string;
  chainId: string;
  totalFeeAmountInUsd?: number | null;
  feeToken?: string;
  feeTokenDecimals?: number;
  feeAmountInGwei?: string;
  referrerId: string;
};

/**
 * Custom Event Type for Spindl Referral Tracking
 */
type SpindlCustomEventsType = {
  type: 'CUSTOM';
  data: {
    name: string;
    properties: MatchaAutoSwapEventProperties;
  };
  identity: {
    address: string;
  };
};

interface CustomEventLogProps {
  quote?: Quote;
  txnHash: string;
  sellTokenDecimals: number;
  buyTokenDecimals: number;
  tokenUsdPrices: TokenUsdPrices;
  address: string;
  chainId: string;
  referrer: string;
  isGasless: boolean;
  status: 'pending' | 'completed';
  currentPrice?: SwapPrice | GaslessPrice;
  fee?: Fee;
}

/**
 * Sends custom events for Spindl referral tracking. It should only log pending
 * and completed Matcha Auto transactions which are monetizable.
 */
export async function logReferralToSpindl({
  quote,
  txnHash,
  sellTokenDecimals,
  buyTokenDecimals,
  tokenUsdPrices,
  address,
  chainId,
  referrer,
  isGasless,
  status,
  currentPrice,
  fee,
}: CustomEventLogProps) {
  let feeData = {};

  if (isGasless) {
    const feeSource = quote?.fees?.zeroExFee || currentPrice?.fees?.zeroExFee || fee;
    const buyToken = quote?.buyToken || currentPrice?.buyToken;

    if (feeSource && buyToken) {
      const isBuyToken = feeSource.token.toLowerCase() === buyToken.toLowerCase();
      const feeTokenDecimals = isBuyToken ? buyTokenDecimals : sellTokenDecimals;
      const usdPrice = isBuyToken ? tokenUsdPrices.buyTokenUsd : tokenUsdPrices.sellTokenUsd;

      feeData = {
        totalFeeAmountInUsd: amountToUsd(feeSource.amount, feeTokenDecimals, usdPrice),
        feeToken: feeSource.token,
        feeTokenDecimals,
        feeAmountInGwei: feeSource.amount,
      };
    }
  }

  const events: SpindlCustomEventsType[] = [
    {
      type: 'CUSTOM',
      data: {
        name: `${status}_swap`,
        properties: {
          txnHash,
          chainId,
          referrerId: referrer,
          ...feeData,
        },
      },
      identity: { address },
    },
  ];
  try {
    const result = await fetch(`${routes.spindl.SWAP_EVENT}`, {
      body: JSON.stringify(events),
      method: 'POST',
    });
    if (!result.ok) {
      throw new Error(`Spindl unknown error :${result.statusText}`);
    }
  } catch (e) {
    if (e instanceof Error) {
      trackError(e, {
        spindl: e.message,
        properties: `${JSON.stringify({ txnHash, chainId, referrerId: referrer, address })}`,
      });
    }
  }
}
